import { useCallback, useEffect, useRef } from 'react'

export const ADAPTIVE_POLL_FAST_MS = 3000
export const ADAPTIVE_POLL_MEDIUM_MS = 5000
export const ADAPTIVE_POLL_SLOW_MS = 10000
export const ADAPTIVE_POLL_MEDIUM_THRESHOLD_MS = 30000
export const ADAPTIVE_POLL_SLOW_THRESHOLD_MS = 60000

interface UseAdaptivePollingOptions {
  enabled: boolean
  scheduleKey?: string | number | boolean | null
  getDelayMs: () => number | null
  refresh: () => Promise<void> | void
  /**
   * Po powrocie do karty odśwież od razu tylko wtedy, gdy od końca ostatniego odświeżenia minęło
   * co najmniej tyle ms (i co najmniej bieżące `getDelayMs()`, które hook czyta też w tym momencie);
   * inaczej odliczanie trwa dalej (Szukaj fracht §13: Alt+Tab nie zjada limitu).
   * Za odświeżenie liczy się też zmiana `scheduleKey` (dane odświeżone inną drogą niż `refresh`, np. nowe
   * Szukaj), niezależnie od `enabled` i widoczności karty — nie samo zamontowanie. Bez żadnego odświeżenia
   * i zmiany klucza (pierwsze pobranie) powrót do karty odświeża od razu.
   */
  minVisibilityRefreshGapMs?: number
}

const isDocumentVisible = () => typeof document === 'undefined' || document.visibilityState === 'visible'

export const resolveAdaptivePollBurstDelay = (
  startedAtMs: number | null | undefined,
  nowMs: number = Date.now()
) => {
  if (startedAtMs == null) {
    return ADAPTIVE_POLL_FAST_MS
  }

  const normalizedStartedAtMs = Number(startedAtMs)
  if (!Number.isFinite(normalizedStartedAtMs)) {
    return ADAPTIVE_POLL_FAST_MS
  }

  const elapsedMs = Math.max(0, nowMs - normalizedStartedAtMs)
  if (elapsedMs < ADAPTIVE_POLL_MEDIUM_THRESHOLD_MS) {
    return ADAPTIVE_POLL_FAST_MS
  }
  if (elapsedMs < ADAPTIVE_POLL_SLOW_THRESHOLD_MS) {
    return ADAPTIVE_POLL_MEDIUM_MS
  }
  return ADAPTIVE_POLL_SLOW_MS
}

export function useAdaptivePolling({
  enabled,
  scheduleKey = null,
  getDelayMs,
  refresh,
  minVisibilityRefreshGapMs,
}: UseAdaptivePollingOptions) {
  const timerRef = useRef<number | null>(null)
  const inFlightRef = useRef(false)
  const needsRefetchRef = useRef(false)
  const enabledRef = useRef(enabled)
  const refreshRef = useRef(refresh)
  const getDelayMsRef = useRef(getDelayMs)
  const lastScheduleKeyRef = useRef(scheduleKey)
  const latestScheduleKeyRef = useRef(scheduleKey)
  const runRefreshRef = useRef<() => void>(() => {})
  const lastRefreshAtRef = useRef<number | null>(null)
  const gapScheduleKeyRef = useRef(scheduleKey)
  const minVisibilityRefreshGapMsRef = useRef(minVisibilityRefreshGapMs)

  useEffect(() => {
    enabledRef.current = enabled
    return () => {
      // Odmontowanie (i zmiana `enabled`, po której efekt od razu ustawia nową wartość): odświeżenie w toku,
      // które skończy się później, nie może zaplanować kolejnego ani uruchomić follow-upu z zamkniętym `refresh`
      // (np. starym tokenem po wylogowaniu) — `.finally` czyta ten ref.
      enabledRef.current = false
    }
  }, [enabled])

  useEffect(() => {
    refreshRef.current = refresh
  }, [refresh])

  useEffect(() => {
    getDelayMsRef.current = getDelayMs
  }, [getDelayMs])

  useEffect(() => {
    latestScheduleKeyRef.current = scheduleKey
  }, [scheduleKey])

  useEffect(() => {
    minVisibilityRefreshGapMsRef.current = minVisibilityRefreshGapMs
  }, [minVisibilityRefreshGapMs])

  useEffect(() => {
    if (Object.is(gapScheduleKeyRef.current, scheduleKey)) {
      return
    }
    gapScheduleKeyRef.current = scheduleKey
    lastRefreshAtRef.current = Date.now()
  }, [scheduleKey])

  const clearTimer = useCallback(() => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const scheduleNext = useCallback(() => {
    clearTimer()
    if (!enabledRef.current || !isDocumentVisible()) {
      return
    }

    const delayMs = getDelayMsRef.current()
    if (!Number.isFinite(delayMs) || Number(delayMs) < 0) {
      return
    }

    timerRef.current = window.setTimeout(() => {
      timerRef.current = null
      runRefreshRef.current()
    }, Number(delayMs))
  }, [clearTimer])

  useEffect(() => {
    runRefreshRef.current = () => {
      clearTimer()
      if (!enabledRef.current || !isDocumentVisible()) {
        return
      }

      if (inFlightRef.current) {
        needsRefetchRef.current = true
        return
      }

      inFlightRef.current = true
      Promise.resolve(refreshRef.current())
        .finally(() => {
          inFlightRef.current = false
          lastRefreshAtRef.current = Date.now()
          if (needsRefetchRef.current && enabledRef.current && isDocumentVisible()) {
            needsRefetchRef.current = false
            runRefreshRef.current()
            return
          }
          scheduleNext()
        })
    }
  }, [clearTimer, scheduleNext])

  const requestImmediateRefresh = useCallback(() => {
    if (!enabledRef.current || !isDocumentVisible()) {
      return
    }
    runRefreshRef.current()
  }, [])

  useEffect(() => {
    if (!enabled) {
      needsRefetchRef.current = false
      clearTimer()
      return
    }

    lastScheduleKeyRef.current = latestScheduleKeyRef.current
    if (!isDocumentVisible()) {
      clearTimer()
      return
    }

    scheduleNext()

    return () => {
      clearTimer()
    }
  }, [clearTimer, enabled, scheduleNext])

  useEffect(() => {
    if (!enabledRef.current || !isDocumentVisible()) {
      lastScheduleKeyRef.current = scheduleKey
      return
    }

    if (Object.is(lastScheduleKeyRef.current, scheduleKey)) {
      return
    }

    lastScheduleKeyRef.current = scheduleKey
    if (inFlightRef.current) {
      needsRefetchRef.current = true
      return
    }

    scheduleNext()
  }, [scheduleKey, scheduleNext])

  useEffect(() => {
    if (typeof document === 'undefined') {
      return
    }

    const handleVisibilityChange = () => {
      clearTimer()
      if (!enabledRef.current || !isDocumentVisible()) {
        return
      }

      const minGapMs = minVisibilityRefreshGapMsRef.current
      const lastRefreshAt = lastRefreshAtRef.current
      if (minGapMs !== undefined && lastRefreshAt !== null) {
        // Przerwa to co najmniej bieżący harmonogram (429 → 60 s, backoff, bezczynność), nie tylko minGapMs.
        // Cofnięty zegar systemowy (elapsed < 0) nie może wydłużyć przerwy ponad ten harmonogram.
        const elapsedMs = Math.max(0, Date.now() - lastRefreshAt)
        const remainingMs = Math.max(minGapMs, getDelayMsRef.current() ?? 0) - elapsedMs
        if (remainingMs > 0) {
          // Odliczanie trwa dalej: odświeżymy, gdy od ostatniego odświeżenia minie cała przerwa.
          timerRef.current = window.setTimeout(() => {
            timerRef.current = null
            runRefreshRef.current()
          }, remainingMs)
          return
        }
      }

      requestImmediateRefresh()
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [clearTimer, requestImmediateRefresh])

  return {
    requestImmediateRefresh,
  }
}
